import { useEffect, useState } from 'react';
import { Card, PageHeader, SkeletonRows } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { agentApi, type AgentStats } from '@/shared/api/agentApi';

/**
 * "הביצועים שלי" (אפיון מסך הנציג, 07/09/2026) — הנתונים האישיים של הנציג
 * בלבד: שיחות שיצאו ונענו, זמן שיחה, המרות ועסקאות. **בלי ייצוא** ובלי
 * השוואה לנציגים אחרים — זו הרשאה של המנהל.
 *
 * הנתונים מגיעים מ-`GET /agent/stats` (אותה נקודה שמזינה את הווידג'ט במסך
 * העבודה), כדי שמספר שהנציג רואה יהיה אותו מספר שהמנהל רואה.
 */
const WEEKDAYS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

function fmtDuration(sec?: number): string {
  if (!sec || sec <= 0) return '0:00';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

function Metric({ label, value, hint, icon, tone }: { label: string; value: string | number; hint?: string; icon?: string; tone?: 'accent' | 'ok' }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        {icon && <Icon name={icon as never} size={14} color={tone === 'ok' ? 'var(--ok)' : tone === 'accent' ? 'var(--accent)' : 'var(--muted)'} />}
        <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)' }}>{label}</span>
      </div>
      <div dir="ltr" style={{ fontSize: 'var(--t-3xl)', fontWeight: 700, lineHeight: 1, textAlign: 'start', color: tone === 'ok' ? 'var(--ok)' : 'var(--text)' }}>{value}</div>
      {hint && <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 6 }}>{hint}</div>}
    </Card>
  );
}

export function MyPerformance() {
  const [stats, setStats] = useState<AgentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    agentApi.stats()
      .then((s) => { if (alive) setStats(s); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const today = stats?.today;
  const week = stats?.week;
  const chart = stats?.week_chart ?? [];
  const peak = Math.max(1, ...chart.map((d) => d.count));
  const answeredPct = today && today.calls > 0 ? Math.round((today.answered / today.calls) * 100) : 0;

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="הביצועים שלי" subtitle="הנתונים שלך בלבד — שיחות, זמן דיבור והמרות. מתעדכן לאורך היום." />

      {loading ? (
        <Card><SkeletonRows rows={4} /></Card>
      ) : error ? (
        <Card><div style={{ color: 'var(--danger)', fontSize: 'var(--t-sm)' }}>שגיאה בטעינת המדדים.</div></Card>
      ) : (
        <>
          <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em' }}>היום</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
            <Metric label="שיחות" value={today?.calls ?? 0} icon="phoneOut" />
            <Metric label="נענו" value={today?.answered ?? 0} hint={`${answeredPct}% מהשיחות`} icon="phoneIn" tone="accent" />
            <Metric label="זמן שיחה" value={fmtDuration(today?.total_talk_sec)} hint={`ממוצע ${fmtDuration(today?.aht_sec)} לשיחה`} icon="clock" />
            <Metric label="עסקאות" value={today?.deals ?? 0} hint={`${today?.conversion_pct ?? 0}% המרה מהשיחות שנענו`} icon="check" tone="ok" />
          </div>

          {week && (
            <>
              <div style={{ fontSize: 'var(--t-xs)', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.04em', marginTop: 4 }}>השבוע</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--gap)' }}>
                <Metric label="שיחות" value={week.calls} />
                <Metric label="נענו" value={week.answered} />
                <Metric label="זמן שיחה" value={fmtDuration(week.total_talk_sec)} />
                <Metric label="עסקאות" value={week.deals} tone="ok" />
              </div>
            </>
          )}

          {chart.length > 0 && (
            <Card>
              <div style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', marginBottom: 12 }}>שיחות לפי יום</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 120 }}>
                {chart.map((d) => (
                  <div key={d.day} style={{ flex: 1, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                    <div dir="ltr" style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginBottom: 4 }}>{d.count || ''}</div>
                    <div
                      title={`${d.day}: ${d.count}`}
                      style={{
                        height: `${Math.max(4, (d.count / peak) * 80)}px`,
                        background: d.count > 0 ? 'var(--accent)' : 'var(--line)',
                        borderRadius: '4px 4px 0 0',
                      }}
                    />
                    <div style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', marginTop: 6 }}>
                      {WEEKDAYS[new Date(d.day).getDay()]}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {(stats?.badges?.length ?? 0) > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {stats!.badges!.map((b) => (
                <span key={b.code} style={{ padding: '4px 12px', borderRadius: 'var(--r-pill)', background: 'var(--accent-soft)', color: 'var(--accent-ink)', fontSize: 'var(--t-sm)', fontWeight: 600 }}>
                  {b.label_he}
                </span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
